import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Bell, CreditCard, Store, Users } from "lucide-react";

import { adminApi } from "@profitlens/api-client";
import { Card, CardContent } from "@profitlens/ui/card";
import { ErrorState } from "@profitlens/ui/error-state";
import { Spinner } from "@profitlens/ui/spinner";

import { KpiCard } from "@/components/kpi-card";
import { PageHeader } from "@/components/page-header";
import { formatDateTime } from "@/lib/format";

export function DashboardPage() {
  const { t } = useTranslation();
  const stats = useQuery({
    queryKey: ["admin", "stats"],
    queryFn: () => adminApi.stats(),
  });

  if (stats.isLoading || !stats.data) {
    return (
      <div className="grid place-items-center py-12">
        <Spinner className="h-5 w-5" />
      </div>
    );
  }
  if (stats.error) {
    return <ErrorState error={stats.error} onRetry={() => stats.refetch()} />;
  }
  const s = stats.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("dashboard.title")}
        subtitle={t("dashboard.asOf", { time: formatDateTime(s.generated_at) })}
      />

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label={t("dashboard.tenantsTotal")}
          value={String(s.tenants_total)}
          hint={t("dashboard.tenantsHint", {
            active: s.tenants_active,
            suspended: s.tenants_suspended,
            closed: s.tenants_closed,
          })}
          icon={Store}
        />
        <KpiCard
          label={t("dashboard.trialing")}
          value={String(s.tenants_trialing)}
          hint={t("dashboard.trialingHint", { pro: s.tenants_pro })}
          icon={CreditCard}
        />
        <KpiCard
          label={t("dashboard.usersTotal")}
          value={String(s.users_total)}
          hint={t("dashboard.usersHint", { d7: s.new_tenants_7d, d30: s.new_tenants_30d })}
          icon={Users}
        />
        <KpiCard
          label={t("dashboard.openAlerts")}
          value={String(s.open_alerts)}
          tone={s.open_alerts > 0 ? "negative" : "default"}
          icon={Bell}
        />
      </section>

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Card className="transition-shadow hover:shadow-pop">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted">{t("dashboard.mrr")}</p>
            <p className="mt-1 text-xl font-semibold tabular-nums">
              {s.mrr} <span className="text-sm font-normal text-muted">{s.currency}</span>
            </p>
            <p className="mt-1 text-xs text-muted">{t("dashboard.mrrHint")}</p>
          </CardContent>
        </Card>
        <Card className="transition-shadow hover:shadow-pop">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted">{t("dashboard.paymentsSucceeded")}</p>
            <p className="mt-1 text-xl font-semibold tabular-nums text-success">
              {s.payments_succeeded_count}{" "}
              <span className="text-sm font-normal text-muted">{t("dashboard.countUnit")}</span>
            </p>
            <p className="mt-1 text-xs text-muted">
              {t("dashboard.paymentsSucceededHint", {
                amount: s.payments_succeeded_amount,
                currency: s.currency,
              })}
            </p>
          </CardContent>
        </Card>
        <Card className="transition-shadow hover:shadow-pop">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted">{t("dashboard.paymentsRefunded")}</p>
            <p className="mt-1 text-xl font-semibold tabular-nums text-warning">
              {s.payments_refunded_count}{" "}
              <span className="text-sm font-normal text-muted">{t("dashboard.countUnit")}</span>
            </p>
            <p className="mt-1 text-xs text-muted">
              {t("dashboard.paymentsRefundedHint", {
                amount: s.payments_refunded_amount,
                currency: s.currency,
              })}
            </p>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
